import React, { useState, useMemo, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { ThemeProvider } from '@mui/material/styles';
import muiTheme from './theme/mui-theme';
// Import Jwt-decode for decoding token
import {
  BudgetContext,
  HeaderContext,
  ChatModelContext,
  HeadingContext,
  LoginContext,
  UserContext,
  HeaderTokenObject,
  HeaderTokenContext,
  SaveAppModalContext,
  PiiContext,
  ModelIdContext,
  WorkflowSelectModalContext,
  WorkFlowContext,
  WorkFlowActionContext,
  DisableWorkflowContext,
  identifiers,
  workFlowActionDataType,
  FileContext,
  NavCollapsedContext,
  LlmSetupBannerContext,
} from './utils/context';
import './App.css';
import { publicRoutes, layoutRoutes, Home } from './routes';
import { getUnauthenticatedRedirectUrl } from './auth/unauthenticated-redirect';
import { Variables } from './modules/end-user/create-your-assistant/create-your-assistant.props';

function App() {
  const [heading, setHeading] = useState('GRIGO');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveAppModal, setSaveAppModal] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [chatModel, setChatModel] = useState('');
  const [piiIdentifiers, setPiiIdentifiers] = useState<identifiers>({
    global: [],
    india: [],
    usa: [],
    pii_config_enabled: false,
  });
  const [promptData, setPromptData] = useState({
    model_id: 0,
    max_tokens: 0,
    temperature: '',
  });
  const [workflowSelectModalOpen, setWorkflowSelectModalOpen] = useState<boolean>(false);
  const [workFlowData, setWorkFlowData] = useState<{
    workflow_id?: string | undefined | number;
    trigger_catalogue_id?: string;
    trigger_config?: {
      interval_range?: number | undefined;
      day_of_month_dropdown?: number;
      time_options?: string;
      timezone_dropdown?: string;
      day_selection?: string;
      toggle_weekends?: boolean;
      child_key?: string;
    };
  }>({});
  const [workFlowActionData, setWorkFlowActionData] = useState<workFlowActionDataType>({});
  const [uploadedFile, setUploadedFile] = useState<Variables[] | null>(null);
  const [user, setUser] = useState({});
  const [HeaderTokens, setHeaderTokens] = useState<HeaderTokenObject>({
    total_budget: 0,
    remaining_budget: 0,
  });
  const [disableMode, setDisableMode] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [showLlmSetupBanner, setShowLlmSetupBanner] = useState(false);
  const contexts = useMemo(
    () => ({
      login: { isLoggedIn, setIsLoggedIn },
      user: { user, setUser },
      heading: { heading, setHeading },
      header: { userName, setUserName },
      headerTokens: { HeaderTokens, setHeaderTokens },
      budget: { isModalOpen, setIsModalOpen },
      saveModal: { saveAppModal, setSaveAppModal },
      chatModel: { chatModel, setChatModel },
      pii: { piiIdentifiers, setPiiIdentifiers },
      model: { promptData, setPromptData },
      workFlowModalValue: {
        workflowSelectModalOpen,
        setWorkflowSelectModalOpen,
      },
      workFlow: { workFlowData, setWorkFlowData },
      workFlowAction: { workFlowActionData, setWorkFlowActionData },
      disableFlow: { disableMode, setDisableMode },
      navCollapsed: { isNavCollapsed: navCollapsed, setIsNavCollapsed: setNavCollapsed },
      llmSetupBanner: { showLlmSetupBanner, setShowLlmSetupBanner },
      file: { uploadedFile, setUploadedFile },
    }),
    [
      isLoggedIn,

      user,
      heading,
      userName,
      HeaderTokens,
      isModalOpen,
      saveAppModal,
      chatModel,
      piiIdentifiers,
      promptData,
      workflowSelectModalOpen,
      workFlowData,
      workFlowActionData,
      disableMode,
      navCollapsed,
      showLlmSetupBanner,
    ],
  );

  // Auth logic moved to Protected route for better performance

  const { getRemainingTime } = useIdleTimer({
    timeout: 1_800_000,
    throttle: 500,
  });

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const interval = setInterval(() => {
      const remainingTimeInSeconds = Math.ceil(getRemainingTime() / 1000);

      if (remainingTimeInSeconds <= 0) {
        clearInterval(interval);
        window.location.assign(getUnauthenticatedRedirectUrl());
      }
    }, 500);

    return () => clearInterval(interval);
  }, [getRemainingTime, isLoggedIn]);

  return (
    <ThemeProvider theme={muiTheme}>
      <LoginContext.Provider value={contexts.login}>
        <UserContext.Provider value={contexts.user}>
          <HeadingContext.Provider value={contexts.heading}>
            <BudgetContext.Provider value={contexts.budget}>
              <HeaderContext.Provider value={contexts.header}>
                <HeaderTokenContext.Provider value={contexts.headerTokens}>
                  <ChatModelContext.Provider value={contexts.chatModel}>
                    <SaveAppModalContext.Provider value={contexts.saveModal}>
                      <PiiContext.Provider value={contexts.pii}>
                        <ModelIdContext.Provider value={contexts.model}>
                          <WorkflowSelectModalContext.Provider value={contexts.workFlowModalValue}>
                            <WorkFlowContext.Provider value={contexts.workFlow}>
                              <WorkFlowActionContext.Provider value={contexts.workFlowAction}>
                                <DisableWorkflowContext.Provider value={contexts?.disableFlow}>
                                  <FileContext.Provider value={contexts?.file}>
                                    <LlmSetupBannerContext.Provider value={contexts.llmSetupBanner}>
                                      <NavCollapsedContext.Provider value={contexts.navCollapsed}>
                                        {useMemo(
                                          () => (
                                            <div className="app">
                                              {/* Avoid full-screen overlay on lazy route transitions (was jarring over nav). */}
                                              <React.Suspense fallback={null}>
                                                <Routes>
                                                  {publicRoutes.map(
                                                    ({ path, component: Component }) => (
                                                      <Route
                                                        key={path}
                                                        path={path}
                                                        element={<Component />}
                                                      />
                                                    ),
                                                  )}
                                                  <Route element={<Home />}>
                                                    {layoutRoutes.map(
                                                      ({ path, component: Component }) => (
                                                        <Route
                                                          key={path}
                                                          path={path}
                                                          element={<Component />}
                                                        />
                                                      ),
                                                    )}
                                                  </Route>
                                                </Routes>
                                              </React.Suspense>
                                            </div>
                                          ),
                                          [],
                                        )}
                                      </NavCollapsedContext.Provider>
                                    </LlmSetupBannerContext.Provider>
                                  </FileContext.Provider>
                                </DisableWorkflowContext.Provider>
                              </WorkFlowActionContext.Provider>
                            </WorkFlowContext.Provider>
                          </WorkflowSelectModalContext.Provider>
                        </ModelIdContext.Provider>
                      </PiiContext.Provider>
                    </SaveAppModalContext.Provider>
                  </ChatModelContext.Provider>
                </HeaderTokenContext.Provider>
              </HeaderContext.Provider>
            </BudgetContext.Provider>
          </HeadingContext.Provider>
        </UserContext.Provider>
      </LoginContext.Provider>
    </ThemeProvider>
  );
}

export default App;
